import React, { useState, useEffect, useRef } from "react";
import UserService from "../services/user.service"; // Ensure this service handles your CRUD operations
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner, Tabs, Tab, Alert, Badge, Row, Col } from 'react-bootstrap';
import { FaEdit, FaTrash, FaInfoCircle } from 'react-icons/fa';
import ErrorModal from './ErrorModal';
import TariffTelephonyOptions from './TariffTelephonyOptions';
import { parseParamValue, serializeParamValue, emptyModuleValue, isOptionsModule } from '../utils/tariffModuleValues';

/** Standard-qutel_config mit sinnvollen WIRRT-/qutel-Vorbelegungen.
 *  Alle kaufmaennischen Kernwerte (Name, Preis, Startguthaben, Laufzeit,
 *  Parallelnutzung, Minuten) werden automatisch aus den Haupt-Reitern
 *  (Allgemein, Laufzeiten, Telefonie) synchronisiert. */
const emptyQutelConfig = () => ({
  sequence_name: "",
  codes_per_page: 1,
  sequence_valid_from: 0,
  sequence_valid_until: 0,
  zone_id: "11",
  payment_type: "pricing",
  max_minutes: 0,
  administration_fee: 0,
  administration_fee_after_secs: 0,
  first_tact_period: 60,
  second_tact_period: 60,
  pricing_out_of_zones: 0,
  randombreak: false,
  randombreak_percent: 0,
  reseller_id: "436",
  reseller_currency: "EUR",
  reseller_vat: 19,
  reseller_billing_without_vat: false,
  recommended_sale_price: 0,
  separation_fixed_value: 0,
  separation_1_starting: 0,
  separation_1_value: 0,
  separation_1_type: "",
  separation_2_starting: 0,
  separation_2_value: 0,
  separation_2_type: "",
  separation_3_starting: 0,
  separation_3_value: 0,
  separation_3_type: "",
  separation_4_starting: 0,
  separation_4_value: 0,
  separation_4_type: "",
  separation_5_starting: 0,
  separation_5_value: 0,
  separation_5_type: "",
  allow_for_registration: true,
  allow_for_registration_web: true,
  allow_for_registration_phone: true,
  registration_rate_code: "",
  registration_credit: 0,
  registration_max_parallel_usage: 1,
  registration_contract_duration: "12m",
  registration_contract_no_charge: false,
  registration_numbers_teltotal: "1",
  registration_numbers_vcard: "0",
  registration_numbers_webservice: "0",
  registration_numbers_accessnumber: "0",
  registration_numbers_callback: "0",
  registration_numbers_roamingkill: "0",
  registration_numbers_noservice: "0",
  allow_for_payment: false,
  allow_for_payment_web: false,
  allow_for_payment_phone: false,
  payment_credit: 0,
  allow_for_sytbox: false,
  sytbox_credit: 0,
  sytbox_numbers: "0",
  allow_sip_name_reservation: false,
  allowed_sip_name_types: "alphanumeric",
  language: "de",
});

/** Konvertiert Laufzeit (Wert + Einheit) aus Reiter "Laufzeiten" in qutel-Format (z. B. 12m). */
const toQutelDurationCode = (val, unit, fallback = "") => {
  const n = parseInt(val, 10);
  if (!Number.isInteger(n) || n <= 0) return fallback;
  switch (String(unit || "").toLowerCase()) {
    case "day": return `${n}d`;
    case "week": return `${n}w`;
    case "month": return `${n}m`;
    case "quarter": return `${n * 3}m`;
    case "year": return `${n}y`;
    default: return fallback;
  }
};

/** Pflichtfeld-Kennzeichnung im Label (roter Stern). */
const Req = ({ children }) => (
  <>
    {children}
    <span className="text-danger" title="Pflichtfeld" aria-label="Pflichtfeld"> *</span>
  </>
);

const BoardTariffs = () => {
  const [tariffs, setTariffs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [deleteState, setDeleteState] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiErrors, setApiErrors] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterTerm, setFilterTerm] = useState('');
  const [areas, setAreas] = useState([]);
  // Produkt- & Service-Stammdaten fuer die abhaengige Auswahl im Tarif-Modal
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  // Dynamische Startguthabenarten aus Stammdaten (z. B. EUR, Minuten, TOKEN)
  const [startCreditTypes, setStartCreditTypes] = useState([]);
  // 1:n Startguthaben-Positionen des aktuellen Tarifs
  const [startCredits, setStartCredits] = useState([
    { id: "init_1", credit_type_id: "", credit_value: "" },
  ]);
  // Modulauswahl (feature_scope): verfuegbare APIs + Module der gewaehlten API.
  const [apis, setApis] = useState([]);
  const [moduleParams, setModuleParams] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  // Modal für Modul-Beschreibung (Erklärung)
  const [selectedModuleForModal, setSelectedModuleForModal] = useState(null);
  const [showModuleModal, setShowModuleModal] = useState(false);

  // Redundante / veraltete Modul-Keys, die in der UI nicht angezeigt werden sollen
  const REDUNDANT_MODULE_KEYS = ['seminar', 'moderation', 'ai_listen', 'ai_speak'];

  // Suchfilter der Endungs-Mehrfachauswahl (Modul Domain-Registrierung).
  const [tldFilter, setTldFilter] = useState('');

  // Gespeicherten Modulwert (param_value) in {limit, tlds} zerlegen.

  // Vorauswahl {pid: {limit, tlds}} aus den Tarif-Parametern (Edit/Klon) bauen.
  const paramValuesFromTariff = (selectedParams) => {
    const map = {};
    (selectedParams || []).forEach((p) => {
      map[p.parameter_id] = parseParamValue(
        p.t_tariff_parameters ? p.t_tariff_parameters.param_value : null, p.param_key
      );
    });
    return map;
  };

  // allowed_values eines Katalog-Moduls als String-Liste (JSON-tolerant).
  const paramAllowedValues = (param) => {
    let av = param && param.allowed_values;
    if (typeof av === 'string') {
      try { av = JSON.parse(av); } catch (_) { av = null; }
    }
    return Array.isArray(av) ? av.map(String) : [];
  };

  const handleOpenModuleModal = (e, param) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedModuleForModal(param);
    setShowModuleModal(true);
  };
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Wird geladen, bitte warten...');
  const [showModal, setShowModal] = useState(false);
  const [errorCode, setApiErrorCode] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); 

  // State for the main form data
  const [formData, setFormData] = useState({
    showcase_id: "",
    service_id: "",
    wirrt_usage_terms: "",
    wirrt_minute_price_eur_net: "",
    tariff_id: "",
    tariff_name: "",
    activation_fee_eur_net: "",
    amount_eur_net: "",
    amount_period_net: "",
    yearly_discount_percent: "",
    validity_area_id: "",
    provision_type: "none",
    // NEUE FELDER HINZUGEFÜGT
    contract_term_value: "",
    contract_term_unit: "",
    is_cancellable: false,
    cancellation_period_value: "",
    cancellation_period_unit: "",
    penalty_amount: "",
    penalty_condition_value: "",
    penalty_condition_unit: "",
    // Telefonie (Entitlement-Policy): Minuten-Budget (leer = unbegrenzt),
    // Parallelnutzung (Pflicht, Standard 1), PIN-Pflicht und Einladungsabrechnung.
    included_minutes: "",
    max_parallel_sessions: "1",
    pin_required: false,
    bill_invited_minutes: false,
    // Modulauswahl (feature_scope): gewaehlte API + ausgewaehlte Modul-Parameter
    // + zulaessige Mengen/Endungen je Modul ({pid: {limit, tlds}}).
    selectedApiId: "",
    parameter_ids: [],
    parameter_values: {},
    // qutel-Serie (Voucher-Generator)
    qutel_sync: false,
    qutel_sequence: "",
    qutel_rate_code: "",
    code_charset: "",
    code_length: "",
    qutel_max_codes: "1000",
    qutel_config: emptyQutelConfig(),
  });
  const [showQutelExpert, setShowQutelExpert] = useState(false);

  // New states for the dynamic provision rule tables
  const [fixedRules, setFixedRules] = useState([]);
  const [percentRules, setPercentRules] = useState([]);
  const [validationError, setValidationError] = useState('');
  // Aktiver Reiter im Tarif-Modal (kontrolliert), damit bei Validierungsfehlern
  // automatisch zum betroffenen Reiter gewechselt werden kann.
  const [activeTab, setActiveTab] = useState('general');
  // qutel-Stammdaten (Reseller, Zonen, Rates, SIP-Typen, Payment-Typen)
  const [qutelMaster, setQutelMaster] = useState({
    resellers: [],
    zones: [],
    rates: [],
    sip_name_types: [],
    payment_types: [],
  });
  const [qutelMasterError, setQutelMasterError] = useState('');
  // Vorlagen der uebergeordneten Stufe zum Klonen (nur Erstellen-Modal).
  const [parentTemplates, setParentTemplates] = useState([]);
  const [cloneSourceId, setCloneSourceId] = useState("");

  // === START: NEUE UND ERSETZTE FUNKTIONEN ===

  const handleAddRule = (type) => {
    const newRule = {
      // temporäre ID für React-Keys
      id: `temp_${Date.now()}`, 
      provision_value: '',
      threshold_volume: '',
      threshold_quantity: ''
    };
    if (type === 'fixed') {
      setFixedRules([...fixedRules, newRule]);
    } else {
      setPercentRules([...percentRules, newRule]);
    }
  };

  const handleRuleChange = (index, type, event) => {
    const { name, value } = event.target;
    if (type === 'fixed') {
      const updatedRules = [...fixedRules];
      updatedRules[index][name] = value;
      setFixedRules(updatedRules);
    } else {
      const updatedRules = [...percentRules];
      updatedRules[index][name] = value;
      setPercentRules(updatedRules);
    }
  };
  
  const handleDeleteRule = (index, type) => {
    if (type === 'fixed') {
      setFixedRules(fixedRules.filter((_, i) => i !== index));
    } else {
      setPercentRules(percentRules.filter((_, i) => i !== index));
    }
  };

  // --- 1:n Startguthaben-Handler ---
  const handleAddStartCredit = () => {
    setStartCredits((prev) => [
      ...prev,
      { id: `sc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, credit_type_id: "", credit_value: "" },
    ]);
  };

  const handleStartCreditChange = (index, field, value) => {
    setStartCredits((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDeleteStartCredit = (index) => {
    setStartCredits((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0
        ? next
        : [{ id: `sc_${Date.now()}`, credit_type_id: "", credit_value: "" }];
    });
  };

  const buildStartCreditsStateFromTariff = (tariff) => {
    const list = Array.isArray(tariff?.start_credits) ? tariff.start_credits : [];
    if (list.length > 0) {
      return list.map((row, idx) => ({
        id: row.tariff_start_credit_id || `sc_${idx}_${Date.now()}`,
        credit_type_id: row.credit_type_id != null ? String(row.credit_type_id) : "",
        credit_value: row.credit_value != null ? String(row.credit_value) : "",
      }));
    }
    if (tariff?.start_credit_type_id != null && tariff?.start_credit_value != null) {
      return [
        {
          id: `sc_legacy_${Date.now()}`,
          credit_type_id: String(tariff.start_credit_type_id),
          credit_value: String(tariff.start_credit_value),
        },
      ];
    }
    return [{ id: `sc_init_${Date.now()}`, credit_type_id: "", credit_value: "" }];
  };
  
  // Ermittelt den aktuellen EUR-Startguthabenwert aus den 1:n-Positionen im Reiter "Allgemein".
  const getEurStartCreditFromState = () => {
    for (const sc of startCredits) {
      if (!sc || !sc.credit_type_id || sc.credit_value === "" || sc.credit_value == null) continue;
      const t = startCreditTypes.find((ct) => String(ct.credit_type_id) === String(sc.credit_type_id));
      const isEur =
        !t ||
        String(t.credit_type_code || "").trim().toUpperCase() === "EUR" ||
        String(t.credit_type_name || "").trim().toUpperCase() === "EUR" ||
        String(t.unit_symbol || "").trim() === "€";
      if (isEur) {
        const v = parseFloat(String(sc.credit_value).replace(",", "."));
        if (Number.isFinite(v) && v >= 0) return v;
      }
    }
    return 0;
  };

  // Synchronisiert die qutel_config automatisch mit den Hauptfeldern aus
  // Allgemein, Laufzeiten und Telefonie (Single Source of Truth).
  const buildAutoSyncedQutelConfig = (fd = formData) => {
    const qc = { ...emptyQutelConfig(), ...(fd.qutel_config || {}) };
    const eurCredit = getEurStartCreditFromState();
    const amountNet = parseFloat(fd.amount_eur_net);
    const activationNet = parseFloat(fd.activation_fee_eur_net);
    const safeAmount = Number.isFinite(amountNet) && amountNet >= 0 ? amountNet : 0;
    const safeActivation = Number.isFinite(activationNet) && activationNet >= 0 ? activationNet : 0;
    const derivedDuration = toQutelDurationCode(fd.contract_term_value, fd.contract_term_unit, "");
    const parallel = parseInt(fd.max_parallel_sessions, 10);
    const incMins = parseInt(fd.included_minutes, 10);

    qc.sequence_name = String(fd.tariff_name || qc.sequence_name || "").trim();
    qc.recommended_sale_price = safeAmount;
    qc.registration_credit = eurCredit;
    qc.registration_contract_duration = derivedDuration || String(qc.registration_contract_duration || "12m").trim();
    qc.registration_max_parallel_usage = Number.isInteger(parallel) && parallel >= 1 ? parallel : 1;
    qc.max_minutes = Number.isInteger(incMins) && incMins > 0 ? incMins * 60 : (Number(qc.max_minutes) || 0);
    qc.registration_contract_no_charge = safeAmount === 0 && safeActivation === 0;
    if (fd.qutel_rate_code) {
      qc.registration_rate_code = String(fd.qutel_rate_code);
    }
    if (!qc.reseller_id) {
      qc.reseller_id = "436";
      qc.reseller_currency = "EUR";
      qc.reseller_vat = 19;
    }
    if (!qc.zone_id) qc.zone_id = "11";
    if (!qc.payment_type) qc.payment_type = "pricing";
    if (qc.codes_per_page === "" || qc.codes_per_page == null) qc.codes_per_page = 1;
    if (qc.first_tact_period === "" || qc.first_tact_period == null) qc.first_tact_period = 60;
    if (qc.second_tact_period === "" || qc.second_tact_period == null) qc.second_tact_period = 60;
    if (!qc.language) qc.language = "de";
    return qc;
  };

  const validateForm = () => {
    setValidationError(''); // Reset validation error
    const newErrors = {};
    
    if (!formData.tariff_name) newErrors.tariff_name = 'Tarifname ist erforderlich';
    if (formData.activation_fee_eur_net === '' || formData.activation_fee_eur_net == null || !Number.isFinite(Number(formData.activation_fee_eur_net)) || Number(formData.activation_fee_eur_net) < 0) newErrors.activation_fee_eur_net = 'Die Aktivierungsgebühr muss eine Zahl sein';
    if (formData.amount_eur_net === '' || formData.amount_eur_net == null || !Number.isFinite(Number(formData.amount_eur_net)) || Number(formData.amount_eur_net) < 0) newErrors.amount_eur_net = 'Der Betrag muss eine Zahl sein';
    if (!formData.amount_period_net) newErrors.amount_period_net = 'Der Abrechnungszeitraum ist erforderlich';
    if (!formData.validity_area_id) newErrors.validity_area_id = 'Gültigkeitsbereich ist erforderlich';

    // Telefonie: Parallelnutzung ist Pflicht und muss eine ganze Zahl >= 1 sein.
    const parallel = parseInt(formData.max_parallel_sessions, 10);
    if (!Number.isInteger(parallel) || parallel < 1) {
      newErrors.max_parallel_sessions = 'Anzahl Parallelnutzung muss mindestens 1 sein.';
    }
    // Minuten-Budget optional; wenn gesetzt, muss es eine nicht-negative Zahl sein.
    if (formData.included_minutes !== "" && formData.included_minutes !== null) {
      const mins = parseInt(formData.included_minutes, 10);
      if (!Number.isInteger(mins) || mins < 0) {
        newErrors.included_minutes = 'Minuten-Budget muss eine nicht-negative Zahl sein (oder leer = unbegrenzt).';
      }
    }
    if (typeof formData.bill_invited_minutes !== 'boolean') {
      newErrors.bill_invited_minutes = 'Einladung berechnen muss ein Ja/Nein-Wert sein.';
    }

    // Codeformat: fuer alle Chargen Pflicht (auch ohne qutel-Sync) -> liegt im Reiter Allgemein.
    if (!formData.code_charset) newErrors.code_charset = 'Zeichensatz ist erforderlich.';
    {
      const cl = parseInt(formData.code_length, 10);
      if (!Number.isInteger(cl) || cl < 1) {
        newErrors.code_length = 'Codelaenge ist erforderlich.';
      } else if (formData.qutel_sync && (cl < 6 || cl > 16)) {
        newErrors.code_length = 'Bei qutel-Sync muss die Codelaenge zwischen 6 und 16 liegen.';
      } else if (cl > 16) {
        newErrors.code_length = 'Codelaenge darf maximal 16 sein.';
      }
    }

    // 1:n Startguthaben validieren (falls Teilangaben vorhanden sind)
    for (let i = 0; i < startCredits.length; i++) {
      const sc = startCredits[i] || {};
      const hasType = String(sc.credit_type_id || "").trim() !== "";
      const hasVal = String(sc.credit_value ?? "").trim() !== "";
      if (!hasType && !hasVal) continue;
      if (!hasType || !hasVal) {
        newErrors.start_credits = `Startguthaben (Zeile ${i + 1}): Bitte sowohl Startguthabenart als auch Wert ausfüllen.`;
        break;
      }
      const numVal = parseFloat(String(sc.credit_value).replace(",", "."));
      if (!Number.isFinite(numVal) || numVal < 0) {
        newErrors.start_credits = `Startguthaben (Zeile ${i + 1}): Der Wert muss eine gültige, nicht negative Zahl sein.`;
        break;
      }
    }

    // qutel-Serie: bei aktivem Sync abgeleitete Werte verwenden und Pflichtfelder pruefen.
    if (formData.qutel_sync) {
      const mc = parseInt(formData.qutel_max_codes || 1000, 10);
      if (!Number.isInteger(mc) || mc < 1) {
        newErrors.qutel_max_codes = 'Max. Codes muss eine positive Zahl sein.';
      }
      const qc = buildAutoSyncedQutelConfig(formData);
      if (!qc.sequence_name) newErrors.sequence_name = 'Serienname ist erforderlich.';
      if (qc.codes_per_page === '' || qc.codes_per_page === null || qc.codes_per_page === undefined) {
        newErrors.codes_per_page = 'Codes pro Bogen ist erforderlich.';
      }
      if (qc.sequence_valid_from === '' || qc.sequence_valid_from === null || qc.sequence_valid_from === undefined) {
        newErrors.sequence_valid_from = 'Gültig ab ist erforderlich (0 = unbegrenzt).';
      }
      if (qc.sequence_valid_until === '' || qc.sequence_valid_until === null || qc.sequence_valid_until === undefined) {
        newErrors.sequence_valid_until = 'Gültig bis ist erforderlich (0 = unbegrenzt).';
      }
      if (!qc.zone_id) newErrors.zone_id = 'Zone ist erforderlich.';
      if (!qc.payment_type) newErrors.payment_type = 'Payment-Typ ist erforderlich.';
      if (!qc.reseller_id) newErrors.reseller_id = 'Reseller ist erforderlich.';
      if (!qc.reseller_currency) newErrors.reseller_currency = 'Reseller-Währung fehlt (Reseller wählen).';
      if (qc.reseller_vat === '' || qc.reseller_vat === null || qc.reseller_vat === undefined) {
        newErrors.reseller_vat = 'Reseller-MwSt. fehlt (Reseller wählen).';
      }
      if (!qc.language) newErrors.language = 'Sprache ist erforderlich.';
    }

    // Plausibilitätsprüfung für Staffeln
    const validateTiers = (rules) => {
      const volumes = new Set();
      const quantities = new Set();
      for (const rule of rules) {
        const volume = parseFloat(rule.threshold_volume);
        const quantity = parseInt(rule.threshold_quantity, 10);
        if (!isNaN(volume) && volume > 0) {
          if (volumes.has(volume)) return false;
          volumes.add(volume);
        }
        if (!isNaN(quantity) && quantity > 0) {
          if (quantities.has(quantity)) return false;
          quantities.add(quantity);
        }
      }
      return true;
    };

    if ((formData.provision_type === 'fixed' || formData.provision_type === 'combined') && !validateTiers(fixedRules)) {
      setValidationError('Jede Staffelzeile muss sich auf einen eindeutig neuen Wert beziehen. Doppelungen in Menge oder Verkaufsvolumen sind nicht zulässig.');
      setActiveTab('provisions');
      return false;
    }

    if ((formData.provision_type === 'percent' || formData.provision_type === 'combined') && !validateTiers(percentRules)) {
      setValidationError('Jede Staffelzeile muss sich auf einen eindeutig neuen Wert beziehen. Doppelungen in Menge oder Verkaufsvolumen sind nicht zulässig.');
      setActiveTab('provisions');
      return false;
    }

    setErrors(newErrors);

    // Pflichtfeld-Fehler global sichtbar machen und zum betroffenen Reiter
    // springen (Fehler standen sonst nur auf evtl. ausgeblendeten Reitern).
    const errorKeys = Object.keys(newErrors);
    if (errorKeys.length > 0) {
      const tabByField = {
        tariff_name: 'general',
        activation_fee_eur_net: 'general',
        amount_eur_net: 'general',
        amount_period_net: 'general',
        validity_area_id: 'general',
        included_minutes: 'telephony',
        max_parallel_sessions: 'telephony',
        bill_invited_minutes: 'telephony',
        start_credits: 'general',
        code_charset: 'general',
        code_length: 'general',
        qutel_max_codes: 'qutel',
        sequence_name: 'qutel',
        codes_per_page: 'qutel',
        zone_id: 'qutel',
        payment_type: 'qutel',
        max_minutes: 'qutel',
        administration_fee: 'qutel',
        administration_fee_after_secs: 'qutel',
        first_tact_period: 'qutel',
        second_tact_period: 'qutel',
        pricing_out_of_zones: 'qutel',
        randombreak_percent: 'qutel',
        reseller_id: 'qutel',
        reseller_currency: 'qutel',
        reseller_vat: 'qutel',
        recommended_sale_price: 'qutel',
        separation_fixed_value: 'qutel',
        language: 'qutel',
        sequence_valid_from: 'qutel',
        sequence_valid_until: 'qutel',
        registration_rate_code: 'qutel',
        registration_credit: 'qutel',
        registration_max_parallel_usage: 'qutel',
        registration_contract_duration: 'qutel',
        payment_credit: 'qutel',
        sytbox_credit: 'qutel',
        sytbox_numbers: 'qutel',
        allowed_sip_name_types: 'qutel',
        registration_numbers_teltotal: 'qutel',
        registration_numbers_vcard: 'qutel',
        registration_numbers_webservice: 'qutel',
        registration_numbers_accessnumber: 'qutel',
        registration_numbers_callback: 'qutel',
        registration_numbers_roamingkill: 'qutel',
        registration_numbers_noservice: 'qutel',
      };
      setActiveTab(tabByField[errorKeys[0]] || 'general');
      setValidationError(
        'Bitte erforderliche Angaben prüfen: ' + errorKeys.map(k => newErrors[k]).join(' • ')
      );
      return false;
    }

    setValidationError('');
    return true;
  };

  const parseStoredQutelConfig = (raw) => {
    const base = emptyQutelConfig();
    if (!raw) return base;
    let obj = raw;
    if (typeof raw === 'string') {
      try {
        obj = JSON.parse(raw);
      } catch (_) {
        return base;
      }
    }
    if (!obj || typeof obj !== 'object') return base;
    return { ...base, ...obj };
  };

  const handleEditClick = (tariff) => {
    // Bestehende Modulauswahl uebernehmen und die API daraus ableiten
    // (Module sind API-spezifisch; alle gewaehlten Module gehoeren zur selben API).
    const selectedParams = tariff.parameters || [];
    const svc = tariff.service || services.find((s) => String(s.service_id) === String(tariff.service_id));
    const derivedApiId = selectedParams.length > 0 && selectedParams[0].api_id != null
      ? String(selectedParams[0].api_id)
      : (svc && svc.api_id != null ? String(svc.api_id) : "");

    const derivedShowcaseId = tariff.showcase_id != null
      ? String(tariff.showcase_id)
      : (svc && svc.showcase_id != null ? String(svc.showcase_id) : "");
    const derivedServiceId = tariff.service_id != null ? String(tariff.service_id) : "";

    setFormData({
      showcase_id: derivedShowcaseId,
      service_id: derivedServiceId,
      wirrt_usage_terms: tariff.wirrt_usage_terms || "",
      wirrt_minute_price_eur_net: tariff.wirrt_minute_price_eur_net ?? "",
      tariff_id: tariff.tariff_id,
      tariff_name: tariff.tariff_name,
      activation_fee_eur_net: tariff.activation_fee_eur_net,
      amount_eur_net: tariff.amount_eur_net,
      amount_period_net: tariff.amount_period_net,
      yearly_discount_percent: tariff.yearly_discount_percent != null ? tariff.yearly_discount_percent : "",
      validity_area_id: tariff.validity_area?.area_id || '',
      provision_type: tariff.provision_type || "none",
      // NEUE FELDER HINZUGEFÜGT (mit Fallback)
      contract_term_value: tariff.contract_term_value || "",
      contract_term_unit: tariff.contract_term_unit || "",
      is_cancellable: tariff.is_cancellable || false,
      cancellation_period_value: tariff.cancellation_period_value || "",
      cancellation_period_unit: tariff.cancellation_period_unit || "",
      penalty_amount: tariff.penalty_amount || "",
      penalty_condition_value: tariff.penalty_condition_value || "",
      penalty_condition_unit: tariff.penalty_condition_unit || "",
      included_minutes: (tariff.included_minutes ?? "") === null ? "" : (tariff.included_minutes ?? ""),
      max_parallel_sessions: tariff.max_parallel_sessions != null ? tariff.max_parallel_sessions : 1,
      pin_required: tariff.pin_required === true || tariff.pin_required === 1,
      bill_invited_minutes:
        tariff.bill_invited_minutes === true || tariff.bill_invited_minutes === 1,
      selectedApiId: derivedApiId,
      parameter_ids: selectedParams.map(p => p.parameter_id),
      parameter_values: paramValuesFromTariff(selectedParams),
      qutel_sync: tariff.qutel_sync === true || tariff.qutel_sync === 1,
      qutel_sequence: tariff.qutel_sequence || "",
      qutel_rate_code: tariff.qutel_rate_code || "",
      code_charset: tariff.code_charset || "",
      code_length: tariff.code_length != null ? tariff.code_length : "",
      qutel_max_codes: tariff.qutel_max_codes != null ? tariff.qutel_max_codes : "1000",
      qutel_config: parseStoredQutelConfig(tariff.qutel_config),
    });
    setShowQutelExpert(false);
    setStartCredits(buildStartCreditsStateFromTariff(tariff));
    // Module der abgeleiteten API nachladen (fuer Checkbox-Darstellung).
    loadModulesForApi(derivedApiId);
    // Regeln aufteilen und in den State laden
    setFixedRules(tariff.provision_rules?.filter(r => r.rule_type === 'fixed') || []);
    setPercentRules(tariff.provision_rules?.filter(r => r.rule_type === 'percent') || []);
    setErrors({});
    setValidationError('');
    setActiveTab('general');
    setSelectedTariff(tariff);
    setShowEditModal(true);
  };

  // Laedt Vorlagen der uebergeordneten Stufe fuer das Klon-Dropdown.
  const fetchParentTemplates = async () => {
    try {
      const response = await UserService.getParentTariffTemplates();
      setParentTemplates(Array.isArray(response.data?.tariffs) ? response.data.tariffs : []);
    } catch (error) {
      setParentTemplates([]);
      // Nicht blockierend – Erstellen ohne Vorlage bleibt moeglich.
      console.error("Tarif-Vorlagen konnten nicht geladen werden:", error);
    }
  };

  // Uebernimmt alle Felder eines Vorlagen-Tarifs in das Erstellen-Formular.
  // Tarifname erhaelt Suffix " (Kopie)"; qutel_sequence wird geleert (neu anlegen).
  const applyCloneFromTariff = (tariff) => {
    if (!tariff) return;
    const selectedParams = tariff.parameters || [];
    const svc = tariff.service || services.find((s) => String(s.service_id) === String(tariff.service_id));
    const derivedApiId = selectedParams.length > 0 && selectedParams[0].api_id != null
      ? String(selectedParams[0].api_id)
      : (svc && svc.api_id != null ? String(svc.api_id) : "");
    const derivedShowcaseId = tariff.showcase_id != null
      ? String(tariff.showcase_id)
      : (svc && svc.showcase_id != null ? String(svc.showcase_id) : "");
    const derivedServiceId = tariff.service_id != null ? String(tariff.service_id) : "";
    const baseName = (tariff.tariff_name || "").toString().trim();
    const copyName = baseName
      ? (baseName.endsWith("(Kopie)") ? baseName : `${baseName} (Kopie)`)
      : "";

    const stripRuleMeta = (rules) =>
      (rules || []).map(({ id, rule_id, tariff_id, createdAt, updatedAt, ...rest }) => ({
        ...rest,
        id: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      }));

    setFormData({
      showcase_id: derivedShowcaseId,
      service_id: derivedServiceId,
      wirrt_usage_terms: "",
      wirrt_minute_price_eur_net: "",
      tariff_id: "",
      tariff_name: copyName,
      activation_fee_eur_net: tariff.activation_fee_eur_net,
      amount_eur_net: tariff.amount_eur_net,
      amount_period_net: tariff.amount_period_net,
      yearly_discount_percent: tariff.yearly_discount_percent != null ? tariff.yearly_discount_percent : "",
      validity_area_id: tariff.validity_area?.area_id || "",
      provision_type: tariff.provision_type || "none",
      contract_term_value: tariff.contract_term_value || "",
      contract_term_unit: tariff.contract_term_unit || "",
      is_cancellable: tariff.is_cancellable || false,
      cancellation_period_value: tariff.cancellation_period_value || "",
      cancellation_period_unit: tariff.cancellation_period_unit || "",
      penalty_amount: tariff.penalty_amount || "",
      penalty_condition_value: tariff.penalty_condition_value || "",
      penalty_condition_unit: tariff.penalty_condition_unit || "",
      included_minutes: (tariff.included_minutes ?? "") === null ? "" : (tariff.included_minutes ?? ""),
      max_parallel_sessions: tariff.max_parallel_sessions != null ? tariff.max_parallel_sessions : 1,
      pin_required: tariff.pin_required === true || tariff.pin_required === 1,
      bill_invited_minutes:
        tariff.bill_invited_minutes === true || tariff.bill_invited_minutes === 1,
      selectedApiId: derivedApiId,
      parameter_ids: selectedParams.map((p) => p.parameter_id),
      parameter_values: paramValuesFromTariff(selectedParams),
      qutel_sync: tariff.qutel_sync === true || tariff.qutel_sync === 1,
      qutel_sequence: "",
      qutel_rate_code: "",
      code_charset: tariff.code_charset || "",
      code_length: tariff.code_length != null ? tariff.code_length : "",
      qutel_max_codes: tariff.qutel_max_codes != null ? tariff.qutel_max_codes : "1000",
      qutel_config: parseStoredQutelConfig(tariff.qutel_config),
    });
    setStartCredits(buildStartCreditsStateFromTariff(tariff));
    loadModulesForApi(derivedApiId);
    setFixedRules(
      stripRuleMeta(tariff.provision_rules?.filter((r) => r.rule_type === "fixed") || [])
    );
    setPercentRules(
      stripRuleMeta(tariff.provision_rules?.filter((r) => r.rule_type === "percent") || [])
    );
    setErrors({});
    setValidationError("");
    setActiveTab("general");
  };

  const handleCloneSourceChange = (e) => {
    const id = e.target.value;
    setCloneSourceId(id);
    if (!id) return;
    const template = parentTemplates.find((t) => String(t.tariff_id) === String(id));
    if (template) applyCloneFromTariff(template);
  };

  const handleCreateClick = () => {
    setFormData({
      showcase_id: "",
      service_id: "",
      wirrt_usage_terms: "",
      wirrt_minute_price_eur_net: "",
      tariff_id: "",
      tariff_name: "",
      activation_fee_eur_net: "",
      amount_eur_net: "",
      amount_period_net: "",
      yearly_discount_percent: "",
      validity_area_id: "",
      provision_type: "none",
      // NEUE FELDER HINZUGEFÜGT
      contract_term_value: "",
      contract_term_unit: "",
      is_cancellable: false,
      cancellation_period_value: "",
      cancellation_period_unit: "",
      penalty_amount: "",
      penalty_condition_value: "",
      penalty_condition_unit: "",
      included_minutes: "",
      max_parallel_sessions: "1",
      pin_required: false,
      bill_invited_minutes: false,
      selectedApiId: "",
      parameter_ids: [],
      parameter_values: {},
      qutel_sync: false,
      qutel_sequence: "",
      qutel_rate_code: "",
      code_charset: "alphanumeric",
      code_length: "10",
      qutel_max_codes: "1000",
      qutel_config: emptyQutelConfig(),
    });
    setShowQutelExpert(false);
    setStartCredits([{ id: `sc_init_${Date.now()}`, credit_type_id: "", credit_value: "" }]);
    setModuleParams([]);
    setFixedRules([]);
    setPercentRules([]);
    setErrors({});
    setValidationError('');
    setActiveTab('general');
    setCloneSourceId("");
    setShowCreateModal(true);
    setSelectedTariff(null);
    fetchParentTemplates();
  };

  const tariffCreateOpened = useRef(false);
  useEffect(() => {
    if (tariffCreateOpened.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("neu") === "1") {
      tariffCreateOpened.current = true;
      handleCreateClick();
    }
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);

    let provision_rules = [];
    if (formData.provision_type === 'fixed' || formData.provision_type === 'combined') {
      provision_rules = [...provision_rules, ...fixedRules.map(r => ({ ...r, rule_type: 'fixed' }))];
    }
    if (formData.provision_type === 'percent' || formData.provision_type === 'combined') {
      provision_rules = [...provision_rules, ...percentRules.map(r => ({ ...r, rule_type: 'percent' }))];
    }

    // Temporäre IDs vor dem Senden entfernen
    const final_provision_rules = provision_rules.map(({ id, ...rest }) => rest);

    // Zulaessige Mengen/Endungen je gewaehltem Modul serialisieren.
    const parameter_values = {};
    formData.parameter_ids.forEach((pid) => {
      const serialized = serializeParamValue(formData.parameter_values[pid], moduleParams.find(param => Number(param.parameter_id) === Number(pid))?.param_key);
      if (serialized !== null) parameter_values[pid] = serialized;
    });

    const final_start_credits = startCredits
      .filter((sc) => String(sc.credit_type_id || "").trim() !== "" && String(sc.credit_value ?? "").trim() !== "")
      .map((sc) => ({
        credit_type_id: parseInt(sc.credit_type_id, 10),
        credit_value: parseFloat(String(sc.credit_value).replace(",", ".")),
      }));

    const syncedQutelConfig = formData.qutel_sync ? buildAutoSyncedQutelConfig(formData) : null;

    const finalFormData = {
      ...formData,
      showcase_id: formData.showcase_id ? parseInt(formData.showcase_id, 10) : null,
      service_id: formData.service_id ? parseInt(formData.service_id, 10) : null,
      start_credits: final_start_credits,
      parameter_values,
      provision_rules: final_provision_rules,
      qutel_sync: !!formData.qutel_sync,
      code_charset: formData.code_charset || null,
      code_length: formData.code_length !== '' ? parseInt(formData.code_length, 10) : null,
      qutel_max_codes: formData.qutel_max_codes !== '' ? parseInt(formData.qutel_max_codes, 10) : 1000,
      qutel_config: syncedQutelConfig,
    };

    try {
      if (selectedTariff) {
        await UserService.updateTariffBoard(selectedTariff.tariff_id, finalFormData);
      } else {
        const { tariff_id, ...newTariffData } = finalFormData;
        await UserService.createTariffBoard(newTariffData);
      }
      setDeleteState(!deleteState);
      selectedTariff ? setShowEditModal(false) : setShowCreateModal(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  /** Nested qutel_config-Feld aendern. */
  const handleQutelConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      qutel_config: {
        ...prev.qutel_config,
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  /** Reseller-Auswahl: currency/vat aus Stammdaten uebernehmen. */
  const handleQutelResellerChange = (e) => {
    const resellerId = e.target.value;
    const reseller = (qutelMaster.resellers || []).find(
      (r) => String(r.id) === String(resellerId)
    );
    setFormData((prev) => ({
      ...prev,
      qutel_config: {
        ...prev.qutel_config,
        reseller_id: resellerId,
        reseller_currency: reseller ? String(reseller.currency || '') : '',
        reseller_vat: reseller && reseller.vat != null ? reseller.vat : '',
      },
    }));
  };

  // Module einer API laden (fuer den Reiter "Module"). Leert die Liste, wenn
  // keine API gewaehlt ist. Fehler werden nicht-blockierend angezeigt.
  const loadModulesForApi = async (apiId) => {
    if (!apiId) {
      setModuleParams([]);
      return;
    }
    setLoadingModules(true);
    try {
      const response = await UserService.getApiParameters(apiId);
      const rawList = Array.isArray(response.data) ? response.data : [];
      const filtered = rawList.filter(m => !REDUNDANT_MODULE_KEYS.includes(m.param_key));
      setModuleParams(filtered);
    } catch (error) {
      setModuleParams([]);
      handleApiError(error);
    } finally {
      setLoadingModules(false);
    }
  };

  // API im Reiter "Module" wechseln: Auswahl der Module zuruecksetzen und
  // Module der neuen API nachladen (Module sind API-spezifisch).
  const handleApiChange = (e) => {
    const apiId = e.target.value;
    setFormData(prev => ({ ...prev, selectedApiId: apiId, parameter_ids: [], parameter_values: {} }));
    loadModulesForApi(apiId);
  };

  // Einzelnes Modul (Parameter) an-/abwaehlen.
  const handleModuleToggle = (parameterId) => {
    setFormData(prev => {
      const pid = parseInt(parameterId, 10);
      const exists = prev.parameter_ids.includes(pid);
      const parameter_ids = exists
        ? prev.parameter_ids.filter(id => id !== pid)
        : [...prev.parameter_ids, pid];
      const parameter_values = { ...prev.parameter_values };
      if (exists) { delete parameter_values[pid]; }
      else { parameter_values[pid] = emptyModuleValue(moduleParams.find(param => Number(param.parameter_id) === pid)?.param_key); }
      return { ...prev, parameter_ids, parameter_values };
    });
  };

  // Zulaessige Menge (limit) oder Endungsliste (tlds) eines Moduls aendern.
  const handleModuleValueChange = (parameterId, field, value) => {
    const pid = parseInt(parameterId, 10);
    setFormData(prev => {
      const current = prev.parameter_values[pid] || emptyModuleValue(moduleParams.find(param => Number(param.parameter_id) === pid)?.param_key);
      return {
        ...prev,
        parameter_values: {
          ...prev.parameter_values,
          [pid]: { ...current, [field]: value },
        },
      };
    });
  };

  // Einzelne Endung in der Mehrfachauswahl an-/abwaehlen.
  const handleModuleTldToggle = (parameterId, tld) => {
    const pid = parseInt(parameterId, 10);
    setFormData(prev => {
      const current = prev.parameter_values[pid] || { limit: '', tlds: [] };
      const exists = current.tlds.includes(tld);
      const tlds = exists
        ? current.tlds.filter(t => t !== tld)
        : [...current.tlds, tld].sort();
      return {
        ...prev,
        parameter_values: {
          ...prev.parameter_values,
          [pid]: { ...current, tlds },
        },
      };
    });
  };

  // === ENDE: NEUE UND ERSETZTE FUNKTIONEN ===

  useEffect(() => {
    fetchTariffs();
    fetchAreas();
    fetchApis();
    fetchProductsAndServices();
    fetchStartCreditTypes();
  }, [deleteState]);

  useEffect(() => {
    if (activeTab === "qutel") {
      fetchQutelMasterdata();
    }
  }, [activeTab]);

  // Produkte (Showcases) und Dienste (Services) laden fuer die abhaengige Auswahl.
  const fetchProductsAndServices = async () => {
    try {
      const [prodRes, svcRes] = await Promise.all([
        UserService.getShowcaseBoard(),
        UserService.getServiceBoard(),
      ]);
      const allProducts = Array.isArray(prodRes.data) ? prodRes.data : [];
      setProducts(allProducts.filter((p) => !p.status || p.status === "ACTIVE"));
      setServices(Array.isArray(svcRes.data) ? svcRes.data : []);
    } catch (error) {
      setProducts([]);
      setServices([]);
    }
  };

  // Startguthabenarten aus Stammdaten laden.
  const fetchStartCreditTypes = async () => {
    try {
      const res = await UserService.getStartCreditTypesBoard();
      setStartCreditTypes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setStartCreditTypes([]);
    }
  };

  // Abhaengige Auswahl: Produkt -> Service
  const filteredServicesForTariff = formData.showcase_id
    ? services.filter((s) => String(s.showcase_id) === String(formData.showcase_id))
    : [];

  const selectedProductForTariff = products.find(
    (p) => String(p.showcase_id) === String(formData.showcase_id)
  );
  const selectedServiceForTariff = services.find(
    (s) => String(s.service_id) === String(formData.service_id)
  );

  const handleTariffProductChange = (e) => {
    const newShowcaseId = e.target.value;
    const matchingServices = newShowcaseId
      ? services.filter((s) => String(s.showcase_id) === String(newShowcaseId))
      : [];
    // Falls genau 1 Service zu diesem Produkt gehoert, direkt vorwaehlen
    const autoService = matchingServices.length === 1 ? matchingServices[0] : null;
    const nextServiceId = autoService ? String(autoService.service_id) : "";
    const nextApiId = autoService && autoService.api_id != null
      ? String(autoService.api_id)
      : formData.selectedApiId;
    const prod = products.find((p) => String(p.showcase_id) === String(newShowcaseId));
    const isWirrt =
      Boolean(autoService && String(autoService.qutel_product_key || "").trim().toUpperCase() === "WIRRT") ||
      String(prod?.title || "").toLowerCase().includes("wirrt");

    setFormData((prev) => ({
      ...prev,
      showcase_id: newShowcaseId,
      service_id: nextServiceId,
      selectedApiId: nextApiId || "",
      qutel_sync: isWirrt ? true : prev.qutel_sync,
      qutel_max_codes: prev.qutel_max_codes || "1000",
      code_charset: prev.code_charset || "alphanumeric",
      code_length: prev.code_length && Number(prev.code_length) >= 6 ? prev.code_length : "10",
    }));
    if (isWirrt) {
      fetchQutelMasterdata();
    }
    if (autoService && autoService.api_id != null && String(autoService.api_id) !== String(formData.selectedApiId)) {
      loadModulesForApi(String(autoService.api_id));
    }
  };

  const handleTariffServiceChange = (e) => {
    const newServiceId = e.target.value;
    const svc = services.find((s) => String(s.service_id) === String(newServiceId));
    const nextApiId = svc && svc.api_id != null ? String(svc.api_id) : formData.selectedApiId;
    const isWirrt = Boolean(
      svc &&
      (String(svc.qutel_product_key || "").trim().toUpperCase() === "WIRRT" ||
        String(svc.service_name || "").toLowerCase().includes("wirrt"))
    );
    setFormData((prev) => ({
      ...prev,
      service_id: newServiceId,
      selectedApiId: nextApiId || "",
      qutel_sync: isWirrt ? true : prev.qutel_sync,
      qutel_max_codes: prev.qutel_max_codes || "1000",
      code_charset: prev.code_charset || "alphanumeric",
      code_length: prev.code_length && Number(prev.code_length) >= 6 ? prev.code_length : "10",
    }));
    if (isWirrt) {
      fetchQutelMasterdata();
    }
    if (svc && svc.api_id != null && String(svc.api_id) !== String(formData.selectedApiId)) {
      loadModulesForApi(String(svc.api_id));
    }
  };

  // Produktspezifische Tab-Sichtbarkeit ermitteln
  const prodTitleLower = String(selectedProductForTariff?.title || "").toLowerCase();
  const svcNameLower = String(selectedServiceForTariff?.service_name || "").toLowerCase();
  const isWirrtSelected = Boolean(
    (selectedServiceForTariff && selectedServiceForTariff.qutel_product_key && String(selectedServiceForTariff.qutel_product_key).trim().toUpperCase() === "WIRRT") ||
    prodTitleLower.includes("wirrt") ||
    svcNameLower.includes("wirrt") ||
    formData.qutel_sync
  );
  const isVoicenSelected = Boolean(
    prodTitleLower.includes("voicen") ||
    svcNameLower.includes("voicen")
  );
  const isSytdomainsSelected = Boolean(
    (selectedServiceForTariff && selectedServiceForTariff.sytdomains_product_key && String(selectedServiceForTariff.sytdomains_product_key).trim() !== "") ||
    prodTitleLower.includes("sytdomain") ||
    svcNameLower.includes("domain")
  );

  const showTelephonyTab = Boolean(isWirrtSelected || isVoicenSelected);
  const showModulesTab = Boolean(
    (selectedServiceForTariff && selectedServiceForTariff.api_id != null) ||
    isWirrtSelected ||
    isVoicenSelected ||
    isSytdomainsSelected ||
    (Array.isArray(formData.parameter_ids) && formData.parameter_ids.length > 0)
  );
  const showQutelTab = Boolean(isWirrtSelected);

  // Wenn der aktive Reiter durch Produkt-/Service-Wechsel ausgeblendet wird, auf "Allgemein" wechseln.
  useEffect(() => {
    if (activeTab === "telephony" && !showTelephonyTab) setActiveTab("general");
    if (activeTab === "modules" && !showModulesTab) setActiveTab("general");
    if (activeTab === "qutel" && !showQutelTab) setActiveTab("general");
  }, [activeTab, showTelephonyTab, showModulesTab, showQutelTab]);

  // Verfuegbare APIs laden (fuer die API-Auswahl im Reiter "Module").
  const fetchApis = async () => {
    try {
      const response = await UserService.getApiRegistry();
      setApis(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      handleApiError(error);
    }
  };

  // qutel-Stammdaten fuer den Reiter "qutel-Serie" laden.
  const fetchQutelMasterdata = async () => {
    setQutelMasterError('');
    try {
      const response = await UserService.getQutelMasterdata();
      const data = response.data || {};
      if (data.available === false) {
        setQutelMaster({
          resellers: [],
          zones: [],
          rates: [],
          sip_name_types: [],
          payment_types: ["pricing", "minutes"],
        });
        setQutelMasterError(data.message || "qutel-Stammdaten nicht verfügbar.");
        return;
      }
      setQutelMaster({
        resellers: Array.isArray(data.resellers) ? data.resellers : [],
        zones: Array.isArray(data.zones) ? data.zones : [],
        rates: Array.isArray(data.rates) ? data.rates : [],
        sip_name_types: Array.isArray(data.sip_name_types) ? data.sip_name_types : [],
        payment_types: Array.isArray(data.payment_types) ? data.payment_types : ['pricing', 'minutes'],
      });
    } catch (error) {
      setQutelMaster({
        resellers: [],
        zones: [],
        rates: [],
        sip_name_types: [],
        payment_types: ['pricing', 'minutes'],
      });
      const msg = error?.response?.data?.message || error?.message || 'qutel-Stammdaten nicht verfügbar.';
      setQutelMasterError(msg);
    }
  };

  const fetchTariffs = async () => {
    setLoading(true);
    try {
      const response = await UserService.getTariffBoard();
      setTariffs(response.data.tariffs);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await UserService.getAreaBoard2();
      setAreas(response.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    let errorMessage = 'Ein unerwarteter Fehler ist aufgetreten.';
  
    if (error.response) {
      const { data, status } = error.response;
      if (data && (data.message || data.error)) {
        errorMessage = data.message || data.error;
      }
    }
     
    setApiErrors(errorMessage);
    setApiErrorCode(`Fehler: ${error.code || 'Unbekannter Fehler'}`);
    setShowModal(true);
  };
  
  const filteredTariffs = tariffs.filter((tariff) =>
    tariff.tariff_name && tariff.tariff_name.toLowerCase().includes(filterTerm.toLowerCase())
  );
  
  const currentTariffs = filteredTariffs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const sortedTariffs = [...currentTariffs].sort((a, b) => {
    const aValue = a[sortConfig.key] || '';
    const bValue = b[sortConfig.key] || '';

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    } else if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0; // Equal values
  });

  const totalPages = Math.ceil(filteredTariffs.length / itemsPerPage);
  
  const handleDeleteClick = async (id, e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await UserService.deleteTariffBoard(id);
      setDeleteState(!deleteState);
    } catch (error) {
      if (error.code === 'ER_ROW_IS_REFERENCED_2') {
        setApiErrors('Dieser Tarif kann nicht gelöscht werden, da er in einer anderen Tabelle referenziert wird. Bitte entfernen Sie zuerst diese Referenzen.');
        handleApiError({ message: 'Dieser Tarif kann nicht gelöscht werden, da er in einer anderen Tabelle referenziert wird.' });
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleBlur = (e) => {
    // Optional: Can be used for validation on field exit
  };

  return (
    <Container>
      <h1 className="mt-5">Tarifübersicht</h1>
      <ErrorModal show={showModal} handleClose={() => setShowModal(false)} errorCode={errorCode} errorMessage={apiErrors} />
      
      <Button variant="info" style={{ marginTop: '20px' }} onClick={handleCreateClick} className="mr-2 mb-2">Erstellen</Button>
      {loading && <Spinner animation="border" role="status"><span className="sr-only">Wird geladen...</span></Spinner>}
      
      <Form.Group controlId="formBasicSearch">
        <Form.Control
          type="text"
          placeholder="Nach Tarifnamen suchen"
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
        />
      </Form.Group>

      <Table striped bordered hover className="mt-4">
        <thead>
          <tr>
            <th onClick={() => handleSort('tariff_id')} style={{ cursor: 'pointer' }}>ID</th>
            <th onClick={() => handleSort('tariff_name')} style={{ cursor: 'pointer' }}>Name</th>
            <th>Produkt / Service</th>
            <th onClick={() => handleSort('activation_fee_eur_net')} style={{ cursor: 'pointer' }}>Aktivierungsgebühr (EUR)</th>
            <th onClick={() => handleSort('amount_eur_net')} style={{ cursor: 'pointer' }}>Betrag (EUR)</th>
            <th onClick={() => handleSort('amount_period_net')} style={{ cursor: 'pointer' }}>Zeitraum</th>
            <th onClick={() => handleSort('yearly_discount_percent')} style={{ cursor: 'pointer' }}>Jahresrabatt</th>
            <th onClick={() => handleSort('provision_type')} style={{ cursor: 'pointer' }}>Provisionstyp</th>
            <th onClick={() => handleSort('validity_area.t_areas_group.area_group_name')} style={{ cursor: 'pointer' }}>Bereichsgruppe</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {sortedTariffs.map((tariff) => {
            const tProd = tariff.showcase || products.find((p) => String(p.showcase_id) === String(tariff.showcase_id || tariff.service?.showcase_id));
            const tSvc = tariff.service || services.find((s) => String(s.service_id) === String(tariff.service_id));
            return (
            <tr key={tariff.tariff_id}>
              <td>{tariff.tariff_id}</td>
              <td>{tariff.tariff_name}</td>
              <td>
                {tProd || tSvc ? (
                  <div className="small">
                    {tProd ? <div><strong>{tProd.title}</strong></div> : null}
                    {tSvc ? <div className="text-muted">{tSvc.service_name}</div> : null}
                  </div>
                ) : (
                  <span className="text-muted small">– nicht zugeordnet –</span>
                )}
              </td>
              <td>{tariff.activation_fee_eur_net}</td>
              <td>{tariff.amount_eur_net}</td>
              <td>{tariff.amount_period_net}</td>
              <td>{Number(tariff.yearly_discount_percent) > 0 ? `${tariff.yearly_discount_percent} %` : '–'}</td>
              <td>{tariff.provision_type}</td>
              <td>{tariff.validity_area?.t_areas_group?.area_group_name || 'k.A.'}</td>
              <td>
                <ButtonGroup>
                  <Button variant="warning" onClick={() => handleEditClick(tariff)} title="Bearbeiten" aria-label="Bearbeiten"><FaEdit /></Button>
                  <Button variant="danger" onClick={(e) => handleDeleteClick(tariff.tariff_id, e)} title="Löschen" aria-label="Löschen"><FaTrash /></Button>
                </ButtonGroup>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>

      <Pagination className="mt-4">
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => setCurrentPage(index + 1)}>
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      <Modal size="lg" show={showCreateModal || showEditModal} onHide={() => {
        setShowCreateModal(false);
        setShowEditModal(false);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedTariff ? 'Tarif bearbeiten' : 'Tarif erstellen'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {validationError && <Alert variant="danger">⚠️ {validationError}</Alert>}
            <p className="text-muted small mb-2">
              Mit <span className="text-danger">*</span> gekennzeichnete Felder sind Pflichtfelder.
            </p>
            {!selectedTariff && (
              <Form.Group controlId="cloneParentTariff" className="mb-3">
                <Form.Label>Von übergeordnetem Tarif übernehmen</Form.Label>
                <Form.Control
                  as="select"
                  value={cloneSourceId}
                  onChange={handleCloneSourceChange}
                  aria-label="Von übergeordnetem Tarif übernehmen"
                >
                  <option value="">— Vorlage wählen (optional) —</option>
                  {parentTemplates.map((t) => (
                    <option key={t.tariff_id} value={t.tariff_id}>
                      {t.tariff_name}
                      {t.validity_area?.t_areas_group?.area_group_name
                        ? ` · ${t.validity_area.t_areas_group.area_group_name}`
                        : ""}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">
                  Alle Reiter werden übernommen. Anschließend können Sie den Tarif individuell anpassen.
                  Der Name erhält den Zusatz „(Kopie)“.
                </Form.Text>
              </Form.Group>
            )}

            {/* 1. Abhaengige Produktauswahl: Produkt auswaehlen -> Service auswaehlen */}
            <fieldset className="border rounded p-3 mb-3 bg-light">
              <legend className="float-none w-auto fs-6 px-2 fw-semibold">
                Produkt- &amp; Service-Zuordnung
              </legend>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="tariffProductSelect">
                    <Form.Label>Produkt auswählen</Form.Label>
                    <Form.Control
                      as="select"
                      name="showcase_id"
                      value={formData.showcase_id || ""}
                      onChange={handleTariffProductChange}
                    >
                      <option value="">Produkt auswählen</option>
                      {products.map((prod) => (
                        <option key={prod.showcase_id} value={prod.showcase_id}>
                          {prod.title}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Text className="text-muted">
                      Auswahl aus systemseitig definierten Hauptprodukten.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="tariffServiceSelect">
                    <Form.Label>Service auswählen</Form.Label>
                    <Form.Control
                      as="select"
                      name="service_id"
                      value={formData.service_id || ""}
                      onChange={handleTariffServiceChange}
                      disabled={!formData.showcase_id}
                    >
                      <option value="">
                        {formData.showcase_id
                          ? (filteredServicesForTariff.length
                              ? "Service auswählen"
                              : "Keine Services für dieses Produkt")
                          : "Bitte zuerst Produkt auswählen"}
                      </option>
                      {filteredServicesForTariff.map((svc) => (
                        <option key={svc.service_id} value={svc.service_id}>
                          {svc.service_name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Text className="text-muted">
                      Steuert die produktspezifischen Reiter und die Tarifauswahl im Chargenmanager.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </fieldset>

            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'general')} id="tariff-edit-tabs" className="mb-3">
              <Tab eventKey="general" title="Allgemein">
                <Form.Group controlId="tariffName">
                  <Form.Label><Req>Tarifname</Req></Form.Label>
                  <Form.Control type="text" name="tariff_name" value={formData.tariff_name} onChange={handleChange} />
                  {errors.tariff_name && <Form.Text className="text-danger">{errors.tariff_name}</Form.Text>}
                </Form.Group>

                <fieldset className="border rounded p-3 my-3">
                  <legend className="float-none w-auto fs-6 px-2">Schnellauswahl für die Abrechnung</legend>
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="button" variant="outline-secondary" onClick={() => setFormData(prev => ({ ...prev, activation_fee_eur_net: '0.00' }))}>Ohne Aktivierungsgebühr</Button>
                    <Button type="button" variant="outline-secondary" onClick={() => setFormData(prev => ({ ...prev, amount_eur_net: '0.00', amount_period_net: prev.amount_period_net || 'monthly' }))}>Ohne Grundgebühr</Button>
                    <Button type="button" variant="outline-secondary" onClick={() => setFormData(prev => ({ ...prev, activation_fee_eur_net: '0.00', amount_eur_net: '0.00', amount_period_net: 'onetime', yearly_discount_percent: '0' }))}>Ohne Berechnung (0 €)</Button>
                    <Button type="button" variant="outline-secondary" onClick={() => setFormData(prev => ({ ...prev, amount_period_net: 'monthly' }))}>Monatlich</Button>
                    <Button type="button" variant="outline-secondary" onClick={() => setFormData(prev => ({ ...prev, amount_period_net: 'yearly' }))}>Jährlich</Button>
                  </div>
                  <Form.Text>Die Auswahl trägt Werte in die Preisfelder ein. Sie können diese anschließend ändern. Verbrauchs- und KI-Anbieterkosten bleiben davon unabhängig; ein qutel-Vertrag wird dadurch nicht geändert.</Form.Text>
                </fieldset>
                <Form.Group controlId="activationFee">
                  <Form.Label><Req>Aktivierungsgebühr (EUR netto)</Req></Form.Label>
                  <Form.Control type="number" step="0.01" name="activation_fee_eur_net" value={formData.activation_fee_eur_net} onChange={handleChange} />
                  {errors.activation_fee_eur_net && <Form.Text className="text-danger">{errors.activation_fee_eur_net}</Form.Text>}
                </Form.Group>

                <Form.Group controlId="amountNet" className="mt-2">
                  <Form.Label><Req>Betrag (EUR netto)</Req></Form.Label>
                  <Form.Control type="number" step="0.01" name="amount_eur_net" value={formData.amount_eur_net} onChange={handleChange} />
                  {errors.amount_eur_net && <Form.Text className="text-danger">{errors.amount_eur_net}</Form.Text>}
                </Form.Group>

                <Form.Group controlId="amountPeriod" className="mt-2">
                  <Form.Label><Req>Abrechnungszeitraum</Req></Form.Label>
                  <Form.Control as="select" name="amount_period_net" value={formData.amount_period_net} onChange={handleChange}>
                    <option value="">Zeitraum auswählen</option>
                    <option value="onetime">Einmalig</option>
                    <option value="daily">Täglich</option>
                    <option value="monthly">Monatlich</option>
                    <option value="yearly">Jährlich</option>
                  </Form.Control>
                  {errors.amount_period_net && <Form.Text className="text-danger">{errors.amount_period_net}</Form.Text>}
                </Form.Group>

                {/* 1:n-Gruppe: Startguthaben (Startguthabenart + Werteingabefeld) */}
                <fieldset className="border rounded p-3 my-3">
                  <legend className="float-none w-auto fs-6 px-2">Startguthaben</legend>
                  <p className="small text-muted mb-2">
                    Optionales Startguthaben für diesen Tarif (1:n – mehrere Startguthabenarten gleichzeitig möglich).
                    Die Arten werden dynamisch unter <strong>Stammdaten → Startguthabenarten</strong> gepflegt.
                  </p>
                  {startCredits.map((sc, idx) => {
                    const selectedIds = startCredits.map((r) => String(r.credit_type_id || ""));
                    const visibleTypes = startCreditTypes.filter(
                      (ct) => ct.is_active || selectedIds.includes(String(ct.credit_type_id))
                    );
                    return (
                      <Row key={sc.id || idx} className="g-2 align-items-end mb-2">
                        <Col xs={12} md={6}>
                          <Form.Group controlId={`startCreditType-${idx}`}>
                            <Form.Label className="small mb-1">Startguthabenart</Form.Label>
                            <Form.Control
                              as="select"
                              value={sc.credit_type_id}
                              onChange={(e) => handleStartCreditChange(idx, "credit_type_id", e.target.value)}
                            >
                              <option value="">— Startguthabenart wählen —</option>
                              {visibleTypes.map((ct) => (
                                <option key={ct.credit_type_id} value={ct.credit_type_id}>
                                  {ct.credit_type_name}
                                  {ct.unit_symbol && ct.unit_symbol !== ct.credit_type_name ? ` (${ct.unit_symbol})` : ""}
                                  {!ct.is_active ? " – inaktiv" : ""}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col xs={9} md={5}>
                          <Form.Group controlId={`startCreditValue-${idx}`}>
                            <Form.Label className="small mb-1">Wert / Betrag</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="z. B. 10.00 oder 100"
                              value={sc.credit_value}
                              onChange={(e) => handleStartCreditChange(idx, "credit_value", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={3} md={1} className="d-flex justify-content-end">
                          <Button
                            type="button"
                            variant="outline-danger"
                            onClick={() => handleDeleteStartCredit(idx)}
                            title="Zeile entfernen"
                            aria-label="Startguthaben-Zeile entfernen"
                          >
                            ✕
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      onClick={handleAddStartCredit}
                    >
                      + Weiteres Startguthaben hinzufügen
                    </Button>
                  </div>
                  {errors.start_credits && (
                    <Form.Text className="text-danger d-block mt-1">{errors.start_credits}</Form.Text>
                  )}
                </fieldset>

                <Form.Group controlId="yearlyDiscountPercent">
                  <Form.Label>Rabatt bei Jahresvorauszahlung (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name="yearly_discount_percent"
                    value={formData.yearly_discount_percent}
                    onChange={handleChange}
                    placeholder="z. B. 20"
                  />
                  <Form.Text className="text-muted">
                    Optionaler prozentualer Rabatt, wenn der Kunde diesen Tarif mit jährlicher Zahlweise wählt (z. B. 20 für 20%).
                  </Form.Text>
                </Form.Group>

                <Form.Group controlId="validityArea" className="mt-2">
                  <Form.Label><Req>Gültigkeitsbereich</Req></Form.Label>
                  <Form.Control as="select" name="validity_area_id" value={formData.validity_area_id} onChange={handleChange}>
                    <option value="">Bereich auswählen</option>
                    {areas.map((area) => (
                      <option key={area.area_id} value={area.area_id}>
                        {area.t_areas_group.area_group_name}
                      </option>
                    ))}
                  </Form.Control>
                  {errors.validity_area_id && <Form.Text className="text-danger">{errors.validity_area_id}</Form.Text>}
                </Form.Group>

                <fieldset className="border rounded p-3 my-3">
                  <legend className="float-none w-auto fs-6 px-2">Codeformat (für alle Chargen Pflicht)</legend>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="codeCharset">
                        <Form.Label><Req>Zeichensatz</Req></Form.Label>
                        <Form.Control as="select" name="code_charset" value={formData.code_charset} onChange={handleChange}>
                          <option value="">wählen</option>
                          <option value="numeric">nur Ziffern</option>
                          <option value="alphanumeric">Ziffern + Kleinbuchstaben</option>
                        </Form.Control>
                        {errors.code_charset && <Form.Text className="text-danger">{errors.code_charset}</Form.Text>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="codeLength">
                        <Form.Label><Req>Codelänge</Req></Form.Label>
                        <Form.Control type="number" min="1" max="16" name="code_length" value={formData.code_length} onChange={handleChange} />
                        {errors.code_length && <Form.Text className="text-danger">{errors.code_length}</Form.Text>}
                        <Form.Text className="text-muted">Bei qutel-Sync: 6–16 Zeichen.</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              </Tab>

              <Tab eventKey="provisions" title="Provisionen">
                <Form.Group controlId="provisionType">
                  <Form.Label>Provisionstyp</Form.Label>
                  <Form.Control as="select" name="provision_type" value={formData.provision_type} onChange={handleChange}>
                    <option value="none">Keine Provision</option>
                    <option value="fixed">Festbetrag</option>
                    <option value="percent">Prozentual</option>
                    <option value="combined">Festbetrag und Prozentual</option>
                  </Form.Control>
                </Form.Group>

                {(formData.provision_type === 'fixed' || formData.provision_type === 'combined') && (
                  <>
                    <h5 className="mt-4">Festbetrag-Provisionen</h5>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Festbetrag in Währung</th>
                          <th>Festbetrag ab Nettoverkaufsvolumen</th>
                          <th>Festbetrag ab Verkaufsmenge</th>
                          <th>Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixedRules.map((rule, index) => (
                          <tr key={rule.id || rule.rule_id}>
                            <td><Form.Control type="number" step="0.01" name="provision_value" value={rule.provision_value} onChange={(e) => handleRuleChange(index, 'fixed', e)} /></td>
                            <td><Form.Control type="number" step="0.01" name="threshold_volume" value={rule.threshold_volume} onChange={(e) => handleRuleChange(index, 'fixed', e)} /></td>
                            <td><Form.Control type="number" name="threshold_quantity" value={rule.threshold_quantity} onChange={(e) => handleRuleChange(index, 'fixed', e)} /></td>
                            <td><Button variant="danger" size="sm" onClick={() => handleDeleteRule(index, 'fixed')}>X</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button variant="secondary" onClick={() => handleAddRule('fixed')}>Zeile hinzufügen</Button>
                  </>
                )}

                {(formData.provision_type === 'percent' || formData.provision_type === 'combined') && (
                  <>
                    <h5 className="mt-4">Prozentuale Provisionen</h5>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Prozentsatz in %</th>
                          <th>ab Nettoverkaufsvolumen (€)</th>
                          <th>ab Verkaufsmenge</th>
                          <th>Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {percentRules.map((rule, index) => (
                          <tr key={rule.id || rule.rule_id}>
                            <td><Form.Control type="number" step="0.01" name="provision_value" value={rule.provision_value} onChange={(e) => handleRuleChange(index, 'percent', e)} /></td>
                            <td><Form.Control type="number" step="0.01" name="threshold_volume" value={rule.threshold_volume} onChange={(e) => handleRuleChange(index, 'percent', e)} /></td>
                            <td><Form.Control type="number" name="threshold_quantity" value={rule.threshold_quantity} onChange={(e) => handleRuleChange(index, 'percent', e)} /></td>
                            <td><Button variant="danger" size="sm" onClick={() => handleDeleteRule(index, 'percent')}>X</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button variant="secondary" onClick={() => handleAddRule('percent')}>Zeile hinzufügen</Button>
                  </>
                )}
              </Tab>
              
              {/* === NEUER REITER "LAUFZEITEN" HINZUGEFÜGT === */}
              <Tab eventKey="terms" title="Laufzeiten und Kündigungsfristen">
                <h5 className="mt-3">Laufzeit</h5>
                <Form.Group controlId="contractTermValue">
                  <Form.Label>Laufzeit (Wert)</Form.Label>
                  <Form.Control type="number" name="contract_term_value" value={formData.contract_term_value} onChange={handleChange} />
                </Form.Group>
                <Form.Group controlId="contractTermUnit">
                  <Form.Label>Laufzeit (Einheit)</Form.Label>
                  <Form.Control as="select" name="contract_term_unit" value={formData.contract_term_unit} onChange={handleChange}>
                    <option value="">Einheit auswählen</option>
                    <option value="day">Tag</option>
                    <option value="week">Woche</option>
                    <option value="month">Monat</option>
                    <option value="quarter">Jahresquartal</option>
                    <option value="year">Jahr</option>
                  </Form.Control>
                </Form.Group>

                <hr />

                <Form.Group controlId="isCancellable" className="mt-3">
                  <Form.Check
                    type="switch"
                    id="is-cancellable-switch"
                    label="Kündbar"
                    name="is_cancellable"
                    checked={formData.is_cancellable}
                    onChange={handleChange}
                  />
                </Form.Group>

                {formData.is_cancellable && (
                  <>
                    <h5 className="mt-4">Kündigungsfristen</h5>
                    <Form.Group controlId="cancellationPeriodValue">
                      <Form.Label>Kündigungsfrist vor Laufzeitende (Wert)</Form.Label>
                      <Form.Control type="number" name="cancellation_period_value" value={formData.cancellation_period_value} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group controlId="cancellationPeriodUnit">
                      <Form.Label>Kündigungsfrist (Einheit)</Form.Label>
                      <Form.Control as="select" name="cancellation_period_unit" value={formData.cancellation_period_unit} onChange={handleChange}>
                        <option value="">Einheit auswählen</option>
                        <option value="day">Tag</option>
                        <option value="week">Woche</option>
                        <option value="month">Monat</option>
                        <option value="quarter">Jahresquartal</option>
                        <option value="year">Jahr</option>
                      </Form.Control>
                    </Form.Group>

                    <h5 className="mt-4">Vertragsstrafe</h5>
                    <Form.Group controlId="penaltyAmount">
                      <Form.Label>Vertragsstrafe Höhe als Betrag</Form.Label>
                      <Form.Control type="number" step="0.01" name="penalty_amount" value={formData.penalty_amount} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group controlId="penaltyConditionValue">
                      <Form.Label>Wenn die Restlaufzeit höher ist als (Wert)</Form.Label>
                      <Form.Control type="number" name="penalty_condition_value" value={formData.penalty_condition_value} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group controlId="penaltyConditionUnit">
                      <Form.Label>Wenn die Restlaufzeit höher ist (Einheit)</Form.Label>
                      <Form.Control as="select" name="penalty_condition_unit" value={formData.penalty_condition_unit} onChange={handleChange}>
                        <option value="">Einheit auswählen</option>
                        <option value="day">Tag</option>
                        <option value="week">Woche</option>
                        <option value="month">Monat</option>
                        <option value="quarter">Jahresquartal</option>
                        <option value="year">Jahr</option>
                      </Form.Control>
                    </Form.Group>
                  </>
                )}
              </Tab>

              {/* === REITER "TELEFONIE" (Entitlement-Policy, abhaengig von Produkt/Service) === */}
              {showTelephonyTab && (
              <Tab eventKey="telephony" title="Telefonie">
                <p className="text-muted mt-3">
                  Diese Werte werden beim Einlösen eines Codes als Berechtigung an voicetreff / wirrt übermittelt.
                </p>

                <Form.Group controlId="includedMinutes">
                  <Form.Label>Minuten-Budget</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="included_minutes"
                    value={formData.included_minutes}
                    onChange={handleChange}
                    placeholder="leer = unbegrenzt"
                  />
                  <Form.Text className="text-muted">Leer lassen für unbegrenzte Minuten.</Form.Text>
                  {errors.included_minutes && <Form.Text className="text-danger d-block">{errors.included_minutes}</Form.Text>}
                </Form.Group>

                <Form.Group controlId="maxParallelSessions" className="mt-3">
                  <Form.Label><Req>Anzahl Parallelnutzung (gleichzeitige Geräte)</Req></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    name="max_parallel_sessions"
                    value={formData.max_parallel_sessions}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Wie viele Geräte gleichzeitig eingeloggt sein dürfen (mindestens 1).
                  </Form.Text>
                  {errors.max_parallel_sessions && <Form.Text className="text-danger d-block">{errors.max_parallel_sessions}</Form.Text>}
                </Form.Group>

                <Form.Group controlId="pinRequired" className="mt-3">
                  <Form.Check
                    type="switch"
                    id="pin-required-switch"
                    label="PIN-Nutzung erforderlich"
                    name="pin_required"
                    checked={formData.pin_required}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Bei aktivierter PIN-Pflicht setzt der Nutzer seine PIN in der voicetreff-Oberfläche;
                    voicetreff meldet den Status (gesetzt: ja/nein) per API zurück.
                  </Form.Text>
                </Form.Group>

                <Form.Group controlId="billInvitedMinutes" className="mt-3">
                  <Form.Check
                    type="switch"
                    id="bill-invited-minutes-switch"
                    label="Einladung berechnen"
                    name="bill_invited_minutes"
                    checked={formData.bill_invited_minutes}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Wenn aktiviert, wird für jede eingeladene Person pro Gesprächsminute eine
                    zusätzliche Minute vom Guthaben des Einladenden abgezogen: ein Gast bedeutet
                    doppelten, zwei Gäste dreifachen Minutenverbrauch.
                  </Form.Text>
                  {errors.bill_invited_minutes && (
                    <Form.Text className="text-danger d-block">
                      {errors.bill_invited_minutes}
                    </Form.Text>
                  )}
                </Form.Group>
              </Tab>
              )}

              {/* === REITER "MODULE" (Funktionsumfang je Tarif, abhaengig von Produkt/Service) === */}
              {showModulesTab && (
              <Tab eventKey="modules" title="Module">
                <p className="text-muted mt-3">
                  Wählen Sie die API und anschließend die Module, die dieser Tarif freischaltet.
                  Module sind API-spezifisch. Nicht verfügbare Module werden vom Partner ignoriert,
                  bis sie verfügbar sind.
                </p>

                <Form.Group controlId="moduleApiSelect">
                  <Form.Label>API</Form.Label>
                  <Form.Control as="select" name="selectedApiId" value={formData.selectedApiId} onChange={handleApiChange}>
                    <option value="">API auswählen</option>
                    {apis.map((api) => (
                      <option key={api.api_id} value={api.api_id}>
                        {api.api_name}{api.enabled ? '' : ' (deaktiviert)'}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                {!formData.selectedApiId && (
                  <Alert variant="info" className="mt-3">Bitte zuerst eine API wählen.</Alert>
                )}

                {formData.selectedApiId && loadingModules && (
                  <div className="mt-3"><Spinner animation="border" size="sm" /> Module werden geladen…</div>
                )}

                {formData.selectedApiId && !loadingModules && moduleParams.length === 0 && (
                  <Alert variant="warning" className="mt-3">Für diese API sind keine Module hinterlegt.</Alert>
                )}

                {formData.selectedApiId && !loadingModules && moduleParams.length > 0 && (
                  <div className="mt-3">
                    {moduleParams.map((param) => {
                      const isImplemented = param.maturity === 'implemented';
                      const isChecked = formData.parameter_ids.includes(param.parameter_id);
                      const value = formData.parameter_values[param.parameter_id] || emptyModuleValue(param.param_key);
                      const isDomainModule = param.param_key === 'domain_registration';
                      const tldOptions = isDomainModule ? paramAllowedValues(param) : [];
                      const filteredTlds = tldOptions.filter(
                        (t) => !tldFilter || t.toLowerCase().includes(tldFilter.toLowerCase().replace(/^\./, ''))
                      );
                      return (
                        <div key={param.parameter_id} className="mb-2 border rounded p-2">
                          <div className="d-flex align-items-center flex-wrap">
                            <Form.Check
                              type="checkbox"
                              id={`module-${param.parameter_id}`}
                              checked={isChecked}
                              onChange={() => handleModuleToggle(param.parameter_id)}
                              label={
                                <span className="me-2">
                                  {param.param_label || param.param_key}{' '}
                                  <Badge bg={isImplemented ? 'success' : 'secondary'} pill>
                                    {isImplemented ? 'verfügbar' : (param.maturity || 'geplant')}
                                  </Badge>
                                </span>
                              }
                            />
                            <Button
                              variant="link"
                              className="p-0 text-info ms-2 d-inline-flex align-items-center"
                              style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                              onClick={(e) => handleOpenModuleModal(e, param)}
                              title="Modulbeschreibung anzeigen"
                              aria-label={`Erklärung zu ${param.param_label || param.param_key} anzeigen`}
                            >
                              <FaInfoCircle size={14} className="me-1" />
                              <span style={{ fontSize: '0.78rem' }}>Erklärung</span>
                            </Button>
                          </div>

                          {isChecked && (
                            <div className="mt-2 ps-4">
                              {isOptionsModule(param.param_key) ? <TariffTelephonyOptions moduleKey={param.param_key} parameterId={param.parameter_id} value={value} onChange={handleModuleValueChange} /> : <>
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                <Form.Label className="mb-0" style={{ fontSize: '0.82rem' }}>
                                  Zulässige Menge
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="1"
                                  size="sm"
                                  style={{ maxWidth: 120 }}
                                  placeholder="unbegrenzt"
                                  value={value.limit}
                                  onChange={(e) =>
                                    handleModuleValueChange(param.parameter_id, 'limit', e.target.value)
                                  }
                                />
                                {param.unit && (
                                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>{param.unit}</span>
                                )}
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  (leer = unbegrenzt)
                                </span>
                              </div>

                              {isDomainModule && (
                                <div className="mt-2">
                                  <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>
                                    Inkludierte Domain-Endungen
                                    <span className="text-muted"> (Mehrfachauswahl; keine Auswahl = alle Endungen)</span>
                                  </Form.Label>
                                  {value.tlds.length > 0 && (
                                    <div className="mb-1 d-flex flex-wrap" style={{ gap: 4 }}>
                                      {value.tlds.map((t) => (
                                        <Badge
                                          key={t}
                                          bg="primary"
                                          style={{ cursor: 'pointer' }}
                                          title="Klicken zum Entfernen"
                                          onClick={() => handleModuleTldToggle(param.parameter_id, t)}
                                        >
                                          .{t} ×
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  {tldOptions.length === 0 ? (
                                    <Alert variant="warning" className="py-1 px-2" style={{ fontSize: '0.8rem' }}>
                                      Keine Endungsliste vom Partner übertragen. Bitte in sytdomains unter
                                      „API-Konfiguration“ den Modul-Katalog erneut übertragen.
                                    </Alert>
                                  ) : (
                                    <>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Endung tippen zum Filtern (z. B. de)"
                                        value={tldFilter}
                                        onChange={(e) => setTldFilter(e.target.value)}
                                        style={{ maxWidth: 280 }}
                                      />
                                      <div
                                        className="border rounded mt-1 p-2"
                                        style={{ maxHeight: 190, overflowY: 'auto', maxWidth: 280 }}
                                      >
                                        {filteredTlds.length === 0 && (
                                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            Keine passende Endung.
                                          </div>
                                        )}
                                        {filteredTlds.slice(0, 400).map((t) => (
                                          <Form.Check
                                            key={t}
                                            type="checkbox"
                                            id={`tld-${param.parameter_id}-${t}`}
                                            label={`.${t}`}
                                            checked={value.tlds.includes(t)}
                                            onChange={() => handleModuleTldToggle(param.parameter_id, t)}
                                          />
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                              </>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Tab>
              )}

              {showQutelTab && (
              <Tab eventKey="qutel" title="qutel-Serie">
                <fieldset className="border rounded p-3 my-3">
                  <legend className="float-none w-auto fs-6 px-2">Zusatzangaben für den wirrt-Tarifvergleich</legend>
                  <p className="small text-muted">
                    Wirrt-Angebote des unter „Eigentümer der selbst registrierten Kunden“ ausgewählten Haupt-VO erscheinen automatisch auf wirrt.com.
                    Maßgeblich ist die Zuordnung dieses Tarifs zu einer gültigen, nicht gesperrten B2C-Charge mit einem digital verfügbaren Wirrt-Dienst.
                    Eine zusätzliche Veröffentlichungsfreigabe ist nicht nötig.
                  </p>
                  <Form.Group controlId="wirrtUsageTerms" className="mt-2">
                    <Form.Label>Leistungen &amp; Verbindungsentgelte (öffentlich, optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      maxLength={6000}
                      name="wirrt_usage_terms"
                      value={formData.wirrt_usage_terms || ''}
                      onChange={handleChange}
                      placeholder="Enthaltene Leistungen, Zielnetze, Taktung, Minutenpreise inkl. MwSt., Einschränkungen und Zusatzkosten"
                    />
                  </Form.Group>
                  <Form.Group controlId="wirrtMinutePrice" className="mt-2">
                    <Form.Label>Einheitlicher Minutenpreis (EUR netto, optional)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.0001"
                      name="wirrt_minute_price_eur_net"
                      value={formData.wirrt_minute_price_eur_net ?? ''}
                      onChange={handleChange}
                    />
                    <Form.Text>
                      Bei unterschiedlichen Zielpreisen leer lassen und die Bruttopreise oben beschreiben. Leer bedeutet nicht kostenlos.
                    </Form.Text>
                  </Form.Group>
                  <Form.Text>
                    Preise erscheinen inklusive der Mehrwertsteuer aus dem EUR-Gültigkeitsbereich. Fehlende Zusatzangaben werden als nicht angegeben angezeigt.
                    Die Zuordnung zur Charge legt den KI- oder Telefonzugang fest. Eine Tarifkopie erscheint, sobald sie einem passenden Angebot zugeordnet ist.
                    Der Vergleich ermöglicht eine unverbindliche Anfrage.
                  </Form.Text>
                </fieldset>

                <hr />

                <p className="text-muted mt-3">
                  <strong>Zentrale Tarif- &amp; Serien-Synchronisation (Single Source of Truth):</strong>{" "}
                  Tarifname, Grundpreis, Aktivierungsgebühr, Startguthaben (EUR), Vertragslaufzeit, Kündigungsfrist
                  und Parallelnutzung werden automatisch aus den Reitern <em>Allgemein</em>, <em>Laufzeiten</em> und{" "}
                  <em>Telefonie</em> übernommen und bei jedem Speichern synchron in <strong>qutel</strong>{" "}
                  (<code>wodatel_rates</code> &amp; <code>wodatel_voucher_sequences</code>) sowie <strong>wirrt.com</strong> angelegt bzw. aktualisiert.
                </p>
                {qutelMasterError && (
                  <Alert variant="warning">{qutelMasterError}</Alert>
                )}

                <Form.Group controlId="qutelSync" className="mb-3 mt-3">
                  <Form.Check
                    type="switch"
                    id="qutel-sync-switch"
                    label="Automatische qutel- & wirrt-Synchronisation aktiv"
                    name="qutel_sync"
                    checked={!!formData.qutel_sync}
                    onChange={handleChange}
                    disabled={!!formData.qutel_sequence}
                  />
                  {(formData.qutel_sequence || formData.qutel_rate_code) && (
                    <Form.Text className="text-muted d-block mt-1">
                      {formData.qutel_sequence && (
                        <span className="me-3">qutel-Seriennummer: <strong>{formData.qutel_sequence}</strong></span>
                      )}
                      {(formData.qutel_rate_code || formData.qutel_config?.registration_rate_code) && (
                        <span>qutel-Tarifcode (wodatel_rates): <strong>{formData.qutel_rate_code || formData.qutel_config?.registration_rate_code}</strong></span>
                      )}
                    </Form.Text>
                  )}
                </Form.Group>

                {formData.qutel_sync && (
                  <>
                    <div className="p-3 mb-3 bg-light border rounded">
                      <div className="fw-semibold mb-2">
                        Automatisch übernommene Tarifwerte (keine doppelte Eingabe nötig)
                      </div>
                      <Row className="small text-secondary g-2">
                        <Col md={4}>
                          <strong>Tarif- / Serienname:</strong><br />
                          <span className="text-dark">{formData.tariff_name || "– (aus Allgemein) –"}</span>
                        </Col>
                        <Col md={4}>
                          <strong>Grundpreis / VK (netto):</strong><br />
                          <span className="text-dark">
                            {formData.amount_eur_net !== "" ? `${formData.amount_eur_net} EUR` : "0.00 EUR"} ({formData.amount_period_net || "–"})
                          </span>
                        </Col>
                        <Col md={4}>
                          <strong>Aktivierungsgebühr (netto):</strong><br />
                          <span className="text-dark">
                            {formData.activation_fee_eur_net !== "" ? `${formData.activation_fee_eur_net} EUR` : "0.00 EUR"}
                          </span>
                        </Col>
                        <Col md={4}>
                          <strong>Startguthaben (EUR):</strong><br />
                          <span className="text-dark">{getEurStartCreditFromState().toFixed(2)} EUR</span>
                        </Col>
                        <Col md={4}>
                          <strong>Vertragslaufzeit:</strong><br />
                          <span className="text-dark">
                            {toQutelDurationCode(formData.contract_term_value, formData.contract_term_unit, "12m (Standard)")}
                          </span>
                        </Col>
                        <Col md={4}>
                          <strong>Max. Parallelnutzung:</strong><br />
                          <span className="text-dark">{formData.max_parallel_sessions || 1}</span>
                        </Col>
                      </Row>
                    </div>

                    <h5 className="mt-3">Basis-Zuordnung für qutel</h5>
                    <Row className="mb-3">
                      <Col md={5}>
                        <Form.Group>
                          <Form.Label><Req>Reseller (Mandant)</Req></Form.Label>
                          <Form.Control as="select" name="reseller_id" value={formData.qutel_config.reseller_id} onChange={handleQutelResellerChange}>
                            <option value="436">Wirt.Com / EDEL Investment GmbH (EUR, MwSt. 19%)</option>
                            {(qutelMaster.resellers || [])
                              .filter((r) => String(r.id) !== "436")
                              .map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.legal_name || r.name} ({r.currency}, MwSt. {r.vat})
                                </option>
                              ))}
                          </Form.Control>
                          {errors.reseller_id && <Form.Text className="text-danger">{errors.reseller_id}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="qutelZone">
                          <Form.Label><Req>Verbindungs-Zone</Req></Form.Label>
                          <Form.Control as="select" name="zone_id" value={formData.qutel_config.zone_id} onChange={handleQutelConfigChange}>
                            {(qutelMaster.zones || []).length === 0 && (
                              <option value="11">Standard-Zone (11)</option>
                            )}
                            {(qutelMaster.zones || []).map((z) => (
                              <option key={z.zone_id} value={z.zone_id}>
                                {z.zone_name || z.zone_id}
                              </option>
                            ))}
                          </Form.Control>
                          {errors.zone_id && <Form.Text className="text-danger">{errors.zone_id}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="qutelMaxCodes">
                          <Form.Label><Req>Max. Codes (Kontingent)</Req></Form.Label>
                          <Form.Control type="number" min="1" name="qutel_max_codes" value={formData.qutel_max_codes} onChange={handleChange} />
                          {errors.qutel_max_codes && <Form.Text className="text-danger">{errors.qutel_max_codes}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        onClick={() => setShowQutelExpert((prev) => !prev)}
                      >
                        {showQutelExpert
                          ? "Erweiterte qutel-Serienparameter ausblenden"
                          : "Erweiterte qutel-Serienparameter (Taktung, Staffeln, Sonderoptionen) anzeigen"}
                      </Button>
                    </div>

                    {showQutelExpert && (
                    <div className="border rounded p-3 bg-white">
                    <h5 className="mt-1">Serie &amp; Bogenformat</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="qutelSequenceName">
                          <Form.Label>Abweichender Serienname (optional)</Form.Label>
                          <Form.Control
                            type="text"
                            name="sequence_name"
                            placeholder={formData.tariff_name || "Wird automatisch aus Tarifname übernommen"}
                            value={formData.qutel_config.sequence_name}
                            onChange={handleQutelConfigChange}
                          />
                          {errors.sequence_name && <Form.Text className="text-danger">{errors.sequence_name}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="codesPerPage">
                          <Form.Label><Req>Codes pro Bogen (0–20)</Req></Form.Label>
                          <Form.Control type="number" min="0" max="20" name="codes_per_page" value={formData.qutel_config.codes_per_page} onChange={handleQutelConfigChange} />
                          {errors.codes_per_page && <Form.Text className="text-danger">{errors.codes_per_page}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="qutelLanguage">
                          <Form.Label><Req>Sprache</Req></Form.Label>
                          <Form.Control type="text" name="language" value={formData.qutel_config.language} onChange={handleQutelConfigChange} />
                          {errors.language && <Form.Text className="text-danger">{errors.language}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="seqValidFrom">
                          <Form.Label><Req>Gültig ab (Unix-Sekunden, 0 = unbegrenzt)</Req></Form.Label>
                          <Form.Control type="number" name="sequence_valid_from" value={formData.qutel_config.sequence_valid_from} onChange={handleQutelConfigChange} />
                          {errors.sequence_valid_from && <Form.Text className="text-danger">{errors.sequence_valid_from}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="seqValidUntil">
                          <Form.Label><Req>Gültig bis (Unix-Sekunden, 0 = unbegrenzt)</Req></Form.Label>
                          <Form.Control type="number" name="sequence_valid_until" value={formData.qutel_config.sequence_valid_until} onChange={handleQutelConfigChange} />
                          {errors.sequence_valid_until && <Form.Text className="text-danger">{errors.sequence_valid_until}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>

                    <h5 className="mt-4">Zahlung &amp; Taktung</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="qutelPaymentType">
                          <Form.Label><Req>Payment-Typ</Req></Form.Label>
                          <Form.Control as="select" name="payment_type" value={formData.qutel_config.payment_type} onChange={handleQutelConfigChange}>
                            {(qutelMaster.payment_types || ['pricing', 'minutes']).map((pt) => (
                              <option key={pt} value={pt}>{pt}</option>
                            ))}
                          </Form.Control>
                          {errors.payment_type && <Form.Text className="text-danger">{errors.payment_type}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mt-4">
                          <Form.Check type="switch" id="reseller-no-vat" label="Abrechnung ohne MwSt." name="reseller_billing_without_vat" checked={!!formData.qutel_config.reseller_billing_without_vat} onChange={handleQutelConfigChange} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label><Req>Einmalbetrag (EUR)</Req></Form.Label>
                          <Form.Control type="number" step="0.01" name="administration_fee" value={formData.qutel_config.administration_fee} onChange={handleQutelConfigChange} />
                          {errors.administration_fee && <Form.Text className="text-danger">{errors.administration_fee}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label><Req>Einmalbetrag nach Sekunden</Req></Form.Label>
                          <Form.Control type="number" name="administration_fee_after_secs" value={formData.qutel_config.administration_fee_after_secs} onChange={handleQutelConfigChange} />
                          {errors.administration_fee_after_secs && <Form.Text className="text-danger">{errors.administration_fee_after_secs}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label><Req>Preis außerhalb Zone (EUR)</Req></Form.Label>
                          <Form.Control type="number" step="0.01" name="pricing_out_of_zones" value={formData.qutel_config.pricing_out_of_zones} onChange={handleQutelConfigChange} />
                          {errors.pricing_out_of_zones && <Form.Text className="text-danger">{errors.pricing_out_of_zones}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label><Req>Erster Takt</Req></Form.Label>
                          <Form.Control type="number" name="first_tact_period" value={formData.qutel_config.first_tact_period} onChange={handleQutelConfigChange} />
                          {errors.first_tact_period && <Form.Text className="text-danger">{errors.first_tact_period}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label><Req>Zweiter Takt</Req></Form.Label>
                          <Form.Control type="number" name="second_tact_period" value={formData.qutel_config.second_tact_period} onChange={handleQutelConfigChange} />
                          {errors.second_tact_period && <Form.Text className="text-danger">{errors.second_tact_period}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mt-4">
                          <Form.Check type="switch" id="randombreak-switch" label="Randombreak" name="randombreak" checked={!!formData.qutel_config.randombreak} onChange={handleQutelConfigChange} />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label><Req>Randombreak %</Req></Form.Label>
                          <Form.Control type="number" name="randombreak_percent" value={formData.qutel_config.randombreak_percent} onChange={handleQutelConfigChange} />
                          {errors.randombreak_percent && <Form.Text className="text-danger">{errors.randombreak_percent}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>

                    <h5 className="mt-4">Staffelung 1–5</h5>
                    <Form.Group className="mb-2">
                      <Form.Label><Req>Festbetrag Staffelung (EUR)</Req></Form.Label>
                      <Form.Control type="number" step="0.01" name="separation_fixed_value" value={formData.qutel_config.separation_fixed_value} onChange={handleQutelConfigChange} />
                      {errors.separation_fixed_value && <Form.Text className="text-danger">{errors.separation_fixed_value}</Form.Text>}
                      <Form.Text className="text-muted d-block">
                        Staffeln 1–5: Werte 0/0 und Typ leer = ungenutzt. Bei Werten &gt; 0 ist der Typ Pflicht.
                      </Form.Text>
                    </Form.Group>
                    {[1, 2, 3, 4, 5].map((idx) => (
                      <Row key={`sep-${idx}`} className="mb-2 align-items-end">
                        <Col md={4}>
                          <Form.Label className="small"><Req>{`Staffel ${idx} ab`}</Req></Form.Label>
                          <Form.Control type="number" name={`separation_${idx}_starting`} placeholder={`Staffel ${idx} ab`} value={formData.qutel_config[`separation_${idx}_starting`]} onChange={handleQutelConfigChange} />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small"><Req>{`Wert ${idx}`}</Req></Form.Label>
                          <Form.Control type="number" step="0.01" name={`separation_${idx}_value`} placeholder={`Wert ${idx} (EUR/%)`} value={formData.qutel_config[`separation_${idx}_value`]} onChange={handleQutelConfigChange} />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small">Typ {idx} (bei Wert &gt; 0)</Form.Label>
                          <Form.Control as="select" name={`separation_${idx}_type`} value={formData.qutel_config[`separation_${idx}_type`]} onChange={handleQutelConfigChange}>
                            <option value="">(leer)</option>
                            <option value="absolute">absolute</option>
                            <option value="percent">percent</option>
                          </Form.Control>
                        </Col>
                      </Row>
                    ))}

                    <h5 className="mt-4">Registrierung &amp; Rufnummern-Optionen</h5>
                    <Form.Check type="switch" id="allow-reg" className="mb-2" label="Registrierung erlauben" name="allow_for_registration" checked={!!formData.qutel_config.allow_for_registration} onChange={handleQutelConfigChange} />
                    <Form.Check type="switch" id="allow-reg-web" className="mb-2" label="Registrierung Web" name="allow_for_registration_web" checked={!!formData.qutel_config.allow_for_registration_web} onChange={handleQutelConfigChange} />
                    <Form.Check type="switch" id="allow-reg-phone" className="mb-2" label="Registrierung Telefon" name="allow_for_registration_phone" checked={!!formData.qutel_config.allow_for_registration_phone} onChange={handleQutelConfigChange} />
                    <Row>
                      {['teltotal', 'vcard', 'webservice', 'accessnumber', 'callback', 'roamingkill', 'noservice'].map((key) => (
                        <Col md={6} key={key}>
                          <Form.Group>
                            <Form.Label><Req>Nummern {key}</Req></Form.Label>
                            <Form.Control type="text" name={`registration_numbers_${key}`} value={formData.qutel_config[`registration_numbers_${key}`]} onChange={handleQutelConfigChange} placeholder="leer erlaubt" />
                            {errors[`registration_numbers_${key}`] && (
                              <Form.Text className="text-danger">{errors[`registration_numbers_${key}`]}</Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>

                    <h5 className="mt-4">Payment / Sytbox / SIP</h5>
                    <Form.Check type="switch" id="allow-pay" className="mb-2" label="Payment erlauben" name="allow_for_payment" checked={!!formData.qutel_config.allow_for_payment} onChange={handleQutelConfigChange} />
                    <Form.Check type="switch" id="allow-pay-web" className="mb-2" label="Payment Web" name="allow_for_payment_web" checked={!!formData.qutel_config.allow_for_payment_web} onChange={handleQutelConfigChange} />
                    <Form.Check type="switch" id="allow-pay-phone" className="mb-2" label="Payment Telefon" name="allow_for_payment_phone" checked={!!formData.qutel_config.allow_for_payment_phone} onChange={handleQutelConfigChange} />
                    <Form.Group>
                      <Form.Label><Req>Payment-Guthaben (EUR)</Req></Form.Label>
                      <Form.Control type="number" step="0.01" name="payment_credit" value={formData.qutel_config.payment_credit} onChange={handleQutelConfigChange} />
                      {errors.payment_credit && <Form.Text className="text-danger">{errors.payment_credit}</Form.Text>}
                    </Form.Group>

                    <Form.Check type="switch" id="allow-syt" className="mb-2 mt-3" label="Sytbox erlauben" name="allow_for_sytbox" checked={!!formData.qutel_config.allow_for_sytbox} onChange={handleQutelConfigChange} />
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label><Req>Sytbox-Guthaben (EUR)</Req></Form.Label>
                          <Form.Control type="number" step="0.01" name="sytbox_credit" value={formData.qutel_config.sytbox_credit} onChange={handleQutelConfigChange} />
                          {errors.sytbox_credit && <Form.Text className="text-danger">{errors.sytbox_credit}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label><Req>Sytbox-Nummern</Req></Form.Label>
                          <Form.Control type="text" name="sytbox_numbers" value={formData.qutel_config.sytbox_numbers} onChange={handleQutelConfigChange} placeholder="leer erlaubt" />
                          {errors.sytbox_numbers && <Form.Text className="text-danger">{errors.sytbox_numbers}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Check type="switch" id="allow-sip" className="mb-2 mt-3" label="SIP-Namen-Reservierung" name="allow_sip_name_reservation" checked={!!formData.qutel_config.allow_sip_name_reservation} onChange={handleQutelConfigChange} />
                    <Form.Group>
                      <Form.Label><Req>Erlaubte SIP-Namenstypen (kommagetrennt oder aus Liste)</Req></Form.Label>
                      <Form.Control
                        as="select"
                        name="allowed_sip_name_types"
                        value={formData.qutel_config.allowed_sip_name_types}
                        onChange={handleQutelConfigChange}
                      >
                        <option value="">wählen / leer</option>
                        {(qutelMaster.sip_name_types || []).map((t) => {
                          const val = typeof t === 'string' ? t : (t.key != null ? t.key : '');
                          const label = typeof t === 'string' ? t : (t.label || t.name || val || 'Standard');
                          return <option key={String(val) + '-' + label} value={val}>{label}</option>;
                        })}
                      </Form.Control>
                      <Form.Control
                        className="mt-2"
                        type="text"
                        name="allowed_sip_name_types"
                        placeholder="oder manuell eingeben (leer erlaubt)"
                        value={formData.qutel_config.allowed_sip_name_types}
                        onChange={handleQutelConfigChange}
                      />
                      {errors.allowed_sip_name_types && <Form.Text className="text-danger">{errors.allowed_sip_name_types}</Form.Text>}
                    </Form.Group>
                    </div>
                    )}
                  </>
                )}
              </Tab>
              )}
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
                Schließen
              </Button>
              <Button variant="primary" type="submit">
                {selectedTariff ? 'Aktualisieren' : 'Erstellen'}
              </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal zur anschaulichen Erklärung eines einzelnen Moduls */}
      <Modal
        show={showModuleModal}
        onHide={() => setShowModuleModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
            <span>{selectedModuleForModal?.param_label || selectedModuleForModal?.param_key}</span>
            <Badge
              bg={selectedModuleForModal?.maturity === 'implemented' ? 'success' : 'secondary'}
              pill
            >
              {selectedModuleForModal?.maturity === 'implemented' ? 'verfügbar' : 'geplant'}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <small className="text-muted">
              Technischer Schlüssel: <code>{selectedModuleForModal?.param_key}</code>
            </small>
          </div>
          <div className="p-3 bg-light rounded border mb-3">
            <strong className="d-block mb-1">Funktionsbeschreibung:</strong>
            <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5', fontSize: '0.95rem' }}>
              {selectedModuleForModal?.param_description || 'Für dieses Modul ist in der Partner-API derzeit keine gesonderte Beschreibung hinterlegt.'}
            </p>
          </div>
          <div className="small text-secondary">
            {selectedModuleForModal?.maturity === 'implemented'
              ? '✓ Dieses Modul ist auf voicetreff.com vollständig implementiert und freischaltbar.'
              : '⏳ Dieses Modul ist im Entwicklungskatalog hinterlegt, befindet sich jedoch derzeit noch im Status "geplant".'}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModuleModal(false)}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BoardTariffs;
